/**
 * Strings for top-level pages (home, activity, notifications, people,
 * companies, permissions, create, groups) and a few page-level cards.
 * English is the source of truth; other locales are type-checked against it.
 * Values may contain `{placeholders}` or be plural objects
 * `{ one: '...', other: '...' }` (pass a `count` param).
 */
export const pages = {
  // Activity page header (niup-activity-page).
  activity: {
    title: 'Activity',
    description: 'Here you find a summary of your recent activities including sessions, logins and data sharing.',
  },

  // Notifications page header (niup-notifications-page).
  notifications: {
    title: 'Notifications',
    description: "Never miss a thing. Here you find a list of all notifications so you can keep track of there's to do.",
  },

  // Notifications popover (niup-notifications-popover + helper).
  notificationsPopover: {
    dialogLabel: 'Notifications',
    headerTitle: 'Notifications',
    tabAll: 'All',
    tabAction: 'Requires action',
    tabSecurity: 'Security',
    empty: 'No notifications.',
    viewAll: 'View all notifications',
    reject: 'Reject',
    accept: 'Accept',
    rejectFrom: 'Reject request from {name}',
    acceptFrom: 'Accept request from {name}',
    errorLoad: 'Failed to load notifications.',
    errorAccept: 'Failed to accept request.',
    errorReject: 'Failed to reject request.',
    // Relative timestamps rendered by the helper.
    justNow: 'Just now',
    minutesAgo: '{count}m ago',
    hoursAgo: '{count}h ago',
    daysAgo: '{count}d ago',
  },

  // "Last activities" sidebar card (niup-last-activities-card).
  lastActivities: {
    title: 'Last activities',
    activeSessions: 'Active sessions',
    loginLog: 'Login log (last 30 days)',
    sharingData: 'Sharing data management (last 30 days)',
  },

  // People page (niup-people-page).
  people: {
    title: 'People',
    description: 'Here you find a complete list of the people to which you are related to.',
    requestRepresentation: 'Request representation',
    about: 'About people',
  },

  // Companies page (niup-companies-page).
  companies: {
    title: 'Companies',
    description: 'Here you find a complete list of the companies to which you are related to.',
    requestRepresentation: 'Request representation',
    registerCompany: 'Register a company',
    cardTitle: 'Companies',
    allRoles: 'All roles',
  },

  // Companies filter bar (niup-companies-filters).
  companiesFilters: {
    allUserTypes: 'All user types',
    owner: 'Owner',
    administrator: 'Administrator',
    user: 'User',
    allStatuses: 'All statuses',
    active: 'Active',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
    selectUserType: 'Select a user type',
    selectRole: 'Select a role',
    selectStatus: 'Select a status',
    searchPlaceholder: 'Enter your search terms',
    search: 'Search',
    resultsLabel: 'Results',
    resultsCount: { one: '{count} Result', other: '{count} Result(s)' },
  },

  // Permission groups list page (niup-permissions-page).
  permissions: {
    title: 'Permission groups',
    description: 'Here you find a list of all the permission groups. Select one to open and manage it.',
    newGroup: 'New group',
    about: 'About permission groups',
    searchPlaceholder: 'Enter your search terms',
    empty: 'No permission groups found.',
    groupCount: { one: '{count} Group', other: '{count} Groups' },
    memberCount: { one: '{count} member', other: '{count} members' },
    fieldCount: { one: '{count} field', other: '{count} fields' },
  },

  // Create company page (niup-create-page).
  create: {
    companyInformation: 'Company information',
    companyInformationDesc: 'Enter the company data in the following fields.',
    nameLabel: 'Name *',
    namePlaceholder: "Enter the company's name",
    emailLabel: 'Email *',
    emailPlaceholder: "Enter the company's email",
    nationalityTitle: 'Nationality and language',
    nationalityDesc: "Add the nationalities and languages that you need. When there's more than 1, you have to select one as the default nationality - to do that, click on the star.",
    selectNationality: 'Select nationality',
    selectLanguage: 'Select language',
    defaultNationality: 'Default nationality',
    setAsDefault: 'Set as default',
    removeNationality: 'Remove nationality',
    activationStatus: 'Activation status',
    stepEnterData: 'Enter company data',
    stepEnterDataDesc: 'Add name, email, nationality and other data',
    stepCreate: 'Create company',
    stepCreateDesc: 'Submit the creation form to receive a verification email',
    stepVerify: 'Verify your email',
    stepVerifyDesc: 'Access the provided email to validate and finish the process',
    nextSteps: 'Next steps?',
    next1: 'Receive a confirmation email',
    next2: 'Click the activation link and finish the process',
    next3: 'Company created - now you can accept representation requests and manage their accesses',
    creating: 'Creating...',
    createCompany: 'Create company',
    errorCreate: 'Failed to create company. Please try again.',
    // Validation (waiting for email) view.
    stepWaiting: 'Waiting for email confirmation',
    stepActivated: 'Company activated',
    stepActivatedDesc: 'Your company will be ready to use',
    whatNext: 'What happens next?',
    whatNext1: 'Check your email',
    whatNext2: 'Click the activation link',
    whatNext3: 'Start managing your company',
  },

  // "Register a company" sidebar CTA (niup-register-company-cta).
  registerCompanyCta: {
    title: 'Do you have a company?',
    description: 'Create and register your companies and start managing their permissions.',
    button: 'Register a company',
  },

  // New / edit permission group page (niup-new-group-page).
  newGroup: {
    titleNew: 'New group',
    titleEdit: 'Edit group',
    description: 'Configure which fields to share with group members.',
    groupDetails: 'Group details',
    nameLabel: 'Name',
    namePlaceholder: 'Group name (min. 3 characters)',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Optional description',
    dataShared: 'Data shared',
    dataSharedDesc: 'Select which data is shared with members of this group.',
    permissionCount: { one: '{count} permission', other: '{count} permissions' },
    countryLabel: 'Country',
    selectCountry: 'Select a country',
    sectionSocials: 'Socials',
    sectionDocuments: 'Documents',
    sectionPersonalData: 'Personal data',
    selectAll: 'Select all',
    people: 'People',
    personCount: { one: '{count} person', other: '{count} people' },
    removeMember: 'Remove member',
    deleteGroup: 'Delete group',
    saveChanges: 'Save changes',
    createGroup: 'Create group',
    deleteTitle: 'Delete group?',
    deleteMessage: 'This will permanently delete this permission group and remove all members.',
    errorLoad: 'Failed to load data.',
    errorSave: 'Failed to save group.',
    errorDelete: 'Failed to delete group.',
  },
};
